import Header from "../components/header";
import mainStore from "../../store/store";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../components/footer";
import Stepper from "../components/stepper";
import moment from "moment";
import axios from "axios";
import axiosInstance from "../../axios-interceptor/axios";
import AvailabilityModal from "../../components/AvailabilityModal";
import { SkeletonCarsList } from "../components/skeleton";
import "../../styles/carCards.css";

const Cars = () => {
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedCarForAvailability, setSelectedCarForAvailability] = useState(null);
  const [availabilityData, setAvailabilityData] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const {
    getAllCars,
    userBooking,
    setUserBooking,
    visitedRoutes,
    setVisitedRoutes,
    getCarImg,
    cachedCars,
    setCachedCars,
    getCarAvailabilityCalendar,
    getAllBookings,
    futureBookings,
  } = mainStore();

  const [allCarsList, setAllCarsList] = useState(cachedCars.length > 0 ? cachedCars : []);
  const [loading, setLoading] = useState(cachedCars.length === 0);
  const fromfind = searchParams.get("find");
  console.log("fromfind", fromfind);

  useEffect(() => {
    // SEO
    document.title = "Best Rental Cars in Perth | Hatchbacks, Sedans, SUVs – TAK8";

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Find the best rental cars in Perth with TAK8. Our fleet of late-model hatchbacks, sedans, and SUVs is perfect for city driving or weekend adventures."
      );
    }

    initializeCars();
    fetchAvailabilityData();

    // Auto-refresh: poll every 10s to keep prices in sync with dashboard changes
    const pollInterval = setInterval(() => {
      refreshCars();
    }, 10000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Force immediate re-fetch when tab becomes visible
        // This ensures price changes from dashboard are reflected instantly
        console.log('👁️ Tab visible — forcing car data refresh');
        const currentBooking = mainStore.getState().userBooking;
        if (currentBooking?.booking?.pickUpDate && currentBooking?.booking?.returnDate) {
          getAllCars(currentBooking.booking.pickUpDate, currentBooking.booking.returnDate).then((res) => {
            if (res && Array.isArray(res) && res.length > 0) {
              const startDateTime = moment(
                `${currentBooking.booking.pickUpDate} ${currentBooking.booking.pickUpTime}`,
                "YYYY-MM-DD hh:mm A"
              );
              const endDateTime = moment(
                `${currentBooking.booking.returnDate} ${currentBooking.booking.returnTime}`,
                "YYYY-MM-DD hh:mm A"
              );
              const durationInHours = endDateTime.diff(startDateTime, "hours");
              const inclusiveDays = durationInHours < 24 ? 1 : Math.ceil(durationInHours / 24);

              const updatedCars = res.map((car) => {
                const rates = car.rates || [];
                const unlimited_rates = car.unlimedRates || [];
                const rate = rates.find(
                  (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
                );
                const unlimited_rate = unlimited_rates.find(
                  (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
                );
                if (rate) {
                  car.defaultRatePerDay = rate.rate;
                  car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
                  car.originalRatePerDay = rate.originalRate || null;
                }
                if (unlimited_rate) {
                  car.defaultUnlimitedRatePerDay = unlimited_rate.rate;
                  car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
                  car.originalUnlimitedRatePerDay = unlimited_rate.originalRate || null;
                }
                const carStatus = (car.status || '').toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
                const isUnavailable = carStatus === 'out-of-service' || carStatus === 'maintenance';
                let unavailableLabel = null;
                if (carStatus === 'out-of-service') unavailableLabel = 'Coming Soon';
                else if (carStatus === 'maintenance') unavailableLabel = 'Under Maintenance';
                return { ...car, isUnavailable, unavailableLabel };
              });
              setAllCarsList(updatedCars);
              setCachedCars(updatedCars);
              console.log('✅ Cars refreshed on tab focus with latest prices');
            }
          }).catch((err) => console.warn('Tab focus refresh failed:', err));
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line
  }, []);

  const fetchAvailabilityData = async () => {
    try {
      const response = await axiosInstance.get("/cars/admin/availability", { timeout: 15000 });
      if (response.data && Array.isArray(response.data)) {
        setAvailabilityData(response.data);
        console.log("✅ Availability data fetched:", response.data);
      }
    } catch (error) {
      console.error("❌ Error fetching availability data:", error);
    }
  };

  // Silent refresh — updates car data without showing skeleton
  const refreshCars = async () => {
    try {
      // Read latest booking data directly from store to avoid stale closures
      const currentState = mainStore.getState();
      const currentBooking = currentState.userBooking;
      const pickUpDate = currentBooking?.booking?.pickUpDate;
      const returnDate = currentBooking?.booking?.returnDate;
      const pickUpTime = currentBooking?.booking?.pickUpTime;
      const returnTime = currentBooking?.booking?.returnTime;

      if (!pickUpDate || !returnDate) return;

      const res = await getAllCars(pickUpDate, returnDate);
      await getAllBookings();
      
      if (res && Array.isArray(res) && res.length > 0) {
        const startDateTime = moment(
          `${pickUpDate} ${pickUpTime}`,
          "YYYY-MM-DD hh:mm A"
        );
        const endDateTime = moment(
          `${returnDate} ${returnTime}`,
          "YYYY-MM-DD hh:mm A"
        );
        const durationInHours = endDateTime.diff(startDateTime, "hours");
        const inclusiveDays = durationInHours < 24 ? 1 : Math.ceil(durationInHours / 24);

        const currentFutureBookings = mainStore.getState().futureBookings || [];

        const updatedCars = res.map((car) => {
          const rates = car.rates || [];
          const unlimited_rates = car.unlimedRates || [];
          const rate = rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );
          const unlimited_rate = unlimited_rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );
          if (rate) {
            car.defaultRatePerDay = rate.rate;
            car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
            if (rate.originalRate) {
              car.originalRatePerDay = rate.originalRate;
            } else {
              car.originalRatePerDay = null;
            }
          }
          if (unlimited_rate) {
            car.defaultUnlimitedRatePerDay = unlimited_rate.rate;
            car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
            if (unlimited_rate.originalRate) {
              car.originalUnlimitedRatePerDay = unlimited_rate.originalRate;
            } else {
              car.originalUnlimitedRatePerDay = null;
            }
          }

          // OLD WEBSITE LOGIC: overlap check
          const overlappingCount = currentFutureBookings.filter((booking) => {
            if (booking.car?._id !== car._id) return false;
            const bookedPickUp = moment(`${booking.pickUpDate} ${booking.pickUpTime || '12:00 AM'}`, "YYYY-MM-DD hh:mm A").toDate();
            const bookedReturn = moment(`${booking.returnDate} ${booking.returnTime || '11:59 PM'}`, "YYYY-MM-DD hh:mm A").toDate();
            const userPickUp = startDateTime.toDate();
            const userReturn = endDateTime.toDate();
            return bookedPickUp <= userReturn && bookedReturn >= userPickUp;
          }).length;

          const totalQuantity = car.totalQuantity || 1;
          const isSoldOut = overlappingCount >= totalQuantity;
          const carStatus = (car.status || '').toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
          const isUnavailable = carStatus === 'out-of-service' || carStatus === 'maintenance';
          let unavailableLabel = null;
          if (carStatus === 'out-of-service') unavailableLabel = 'Coming Soon';
          else if (carStatus === 'maintenance') unavailableLabel = 'Under Maintenance';
          else if (isSoldOut) unavailableLabel = 'Sold Out';

          return { ...car, overlappingCount, isSold: isSoldOut, isUnavailable, unavailableLabel };
        });
        setAllCarsList(updatedCars);
        setCachedCars(updatedCars);
        console.log('🔄 Cars list silently refreshed');
      }
    } catch (error) {
      console.warn('Silent refresh failed:', error.message);
    }
  };

  const handleViewAvailability = async (car) => {
    try {
      console.log('📅 Loading availability for:', car.name, car._id);
      // Use the destructured store function
      const calendar = await getCarAvailabilityCalendar(car._id);
      console.log('📅 Calendar data:', calendar);
      
      if (calendar) {
        setSelectedCarForAvailability({
          ...car,
          bookedPeriods: calendar.bookedPeriods || [],
          fullyBookedPeriods: calendar.fullyBookedPeriods || [],
          nextAvailableDate: calendar.nextAvailableDate,
          lastAvailableDate: calendar.lastAvailableDate,
          totalQuantity: calendar.totalQuantity,
        });
        setShowAvailabilityModal(true);
      } else {
        console.log("⚠️ No availability info found for:", car.name);
        // Still open modal with empty data so user knows
        setSelectedCarForAvailability({
          ...car,
          bookedPeriods: [],
          fullyBookedPeriods: [],
          nextAvailableDate: null,
          lastAvailableDate: null,
        });
        setShowAvailabilityModal(true);
      }
    } catch (error) {
      console.error('Error loading availability:', error);
    }
  };

  const initializeCars = async () => {
    if (cachedCars.length === 0) {
      setLoading(true);
    }
    try {
      const updatedRoutes = [...visitedRoutes, "car"];
      setVisitedRoutes(updatedRoutes);

      // Fetch all cars and all bookings
      const res = await getAllCars(userBooking.booking.pickUpDate, userBooking.booking.returnDate);
      await getAllBookings();

      const startDateTime = moment(
        `${userBooking.booking.pickUpDate} ${userBooking.booking.pickUpTime}`,
        "YYYY-MM-DD hh:mm A"
      );
      const endDateTime = moment(
        `${userBooking.booking.returnDate} ${userBooking.booking.returnTime}`,
        "YYYY-MM-DD hh:mm A"
      );

      const durationInHours = endDateTime.diff(startDateTime, "hours");
      const inclusiveDays = durationInHours < 24 ? 1 : Math.ceil(durationInHours / 24);

      userBooking.booking.days = inclusiveDays;
      userBooking.booking.driverAge = 21;

      // Get the latest futureBookings from the store
      const currentFutureBookings = mainStore.getState().futureBookings || [];

      // Safety check: ensure res is an array before mapping
      if (res && Array.isArray(res) && res.length > 0) {
        const bookedCars = res.map((car) => {
          const rates = car.rates || [];
          const unlimited_rates = car.unlimedRates || [];

          const rate = rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );
          const unlimited_rate = unlimited_rates.find(
            (r) => inclusiveDays >= r.minDays && (r.maxDays === null || inclusiveDays <= r.maxDays)
          );

          if (rate) {
            car.defaultRatePerDay = rate.rate;
            car.displayRateLabel = rate.title || `${rate.minDays}+ Days Rate`;
            if (rate.originalRate) {
              car.originalRatePerDay = rate.originalRate;
            } else {
              car.originalRatePerDay = null;
            }
          }

          if (unlimited_rate) {
            car.defaultUnlimitedRatePerDay = unlimited_rate.rate;
            car.displayUnlimitedRateLabel = unlimited_rate.title || `${unlimited_rate.minDays}+ Days Rate`;
            if (unlimited_rate.originalRate) {
              car.originalUnlimitedRatePerDay = unlimited_rate.originalRate;
            } else {
              car.originalUnlimitedRatePerDay = null;
            }
          }

          // OLD WEBSITE LOGIC: Count overlapping bookings for this car
          const overlappingCount = currentFutureBookings.filter((booking) => {
            // Only count bookings for THIS specific car
            if (booking.car?._id !== car._id) return false;

            // Parse existing booking dates
            const bookedPickUp = moment(
              `${booking.pickUpDate} ${booking.pickUpTime || '12:00 AM'}`,
              "YYYY-MM-DD hh:mm A"
            ).toDate();
            const bookedReturn = moment(
              `${booking.returnDate} ${booking.returnTime || '11:59 PM'}`,
              "YYYY-MM-DD hh:mm A"
            ).toDate();

            // Parse user's selected dates
            const userPickUp = startDateTime.toDate();
            const userReturn = endDateTime.toDate();

            // Check overlap: booking starts before user returns AND booking ends after user picks up
            return bookedPickUp <= userReturn && bookedReturn >= userPickUp;
          }).length;

          const totalQuantity = car.totalQuantity || 1;
          const isSoldOut = overlappingCount >= totalQuantity;

          // Also check car status (maintenance, out-of-service)
          const carStatus = (car.status || '').toLowerCase().replace(/_/g, '-').replace(/\s+/g, '-');
          const isUnavailable = carStatus === 'out-of-service' || carStatus === 'maintenance';

          let unavailableLabel = null;
          if (carStatus === 'out-of-service') unavailableLabel = 'Coming Soon';
          else if (carStatus === 'maintenance') unavailableLabel = 'Under Maintenance';
          else if (isSoldOut) unavailableLabel = 'Sold Out';

          return {
            ...car,
            overlappingCount,
            isSold: isSoldOut,
            isUnavailable,
            unavailableLabel,
          };
        });

        setAllCarsList(bookedCars);
        setCachedCars(bookedCars);
      }
    } catch (error) {
      console.error("Error initializing cars:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSelectingCar = (car, type) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    let booking = { ...userBooking };
    booking.car = { ...car, type };

    booking.selectedRates = type === "limited" ? car.rates : car.unlimedRates;
    booking.selectedType = type;
    booking.hasDiscount = !!car.activeDiscount;

    setUserBooking(booking);
    navigate(`/booking?id=${car._id}`);
  };

  if (loading) {
    return (
      <>
        <Header />
        <SkeletonCarsList />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      <section className="MainSection">
        <div className="container">
          <h1 className="Head_2">Cars</h1>
        </div>
      </section>

      <section>
        <div className="container">
          <Stepper />

          <div className="row">
            {allCarsList.map((car, index) => (
              <div className="col-md-4" key={car._id || index}>
                <div className={`CarBox ${car.isUnavailable ? "car-unavailable" : ""}`}>
                  {!car.isUnavailable && car.activeDiscount && (
                    <div className="discount-banner-header">
                      <span className="discount-name">{car.activeDiscount.name}</span>
                      <span className="discount-percentage">{car.activeDiscount.badge}</span>
                    </div>
                  )}

                  <div className="Image">
                    <img src={getCarImg(car)} alt="car" />
                    {car.isAutomatic ? (
                      <span className="Tag Red">Automatic</span>
                    ) : (
                      <span className="Tag Yellow">Manual</span>
                    )}
                    <span className="Feature">
                      <img src="img/icon/carseat.svg" alt="Icon" /> {car.seats} Seats |
                      <img src="img/icon/bags.svg" alt="Icon" /> {car.bags} Bags
                    </span>
                  </div>

                  <div className="Content">
                    <div className="Heading">
                      <h4 className="Head_3">{car.name}</h4>
                    </div>

                    <div className="PriceMain">
                      {/* LIMITED */}
                      <div className="Price">
                        <div>
                          {car.originalRatePerDay && !car.isUnavailable && (
                            <span className="savings">
                              Save ${(car.originalRatePerDay - car.defaultRatePerDay).toFixed(2)}
                            </span>
                          )}

                          {car.seasonalPricing && !car.originalRatePerDay && !car.isUnavailable && (
                            <span className="savings" style={{ background: '#f59e0b', color: '#fff' }}>
                              {car.seasonalPricing.type === 'percentage' 
                                ? `Peak Season +${car.seasonalPricing.value}%` 
                                : 'Seasonal Rate'}
                            </span>
                          )}

                          {car.originalRatePerDay && !car.isUnavailable ? (
                            <>
                              <h5 className="original-price">${car.originalRatePerDay}</h5>
                              <h5 className="discounted-price">${car.defaultRatePerDay}</h5>
                            </>
                          ) : (
                            <h5>${car.defaultRatePerDay || "0.00"}</h5>
                          )}

                          <span className="rate-label">{car.displayRateLabel || "7+ Days Rate"}</span>
                        </div>

                        <p>150KM / Day</p>

                        <a
                          data-analytics-name={`Select Car - ${car.name} - Limited KM`}
                          onClick={() => {
                            if (!car.isUnavailable && !car.isSold) onSelectingCar(car, "limited");
                          }}
                          className={`BtnBorderY cursor-pointer ${
                            car.isSold || car.isUnavailable ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Sold Out" : "Select")}
                        </a>

                        {car.isSold && !car.isUnavailable && (
                          <span
                            data-analytics-name={`View Availability - ${car.name} - Limited`}
                            className="view-availability-link"
                            onClick={() => handleViewAvailability(car)}
                          >
                            View Availability
                          </span>
                        )}
                      </div>

                      {/* UNLIMITED */}
                      <div className="Price">
                        <div>
                          {car.originalUnlimitedRatePerDay && !car.isUnavailable && (
                            <span className="savings">
                              Save ${(car.originalUnlimitedRatePerDay - car.defaultUnlimitedRatePerDay).toFixed(2)}
                            </span>
                          )}

                          {car.seasonalPricing && !car.originalUnlimitedRatePerDay && !car.isUnavailable && (
                            <span className="savings" style={{ background: '#f59e0b', color: '#fff' }}>
                              {car.seasonalPricing.type === 'percentage' 
                                ? `Peak Season +${car.seasonalPricing.value}%` 
                                : 'Seasonal Rate'}
                            </span>
                          )}

                          {car.originalUnlimitedRatePerDay && !car.isUnavailable ? (
                            <>
                              <h5 className="original-price">${car.originalUnlimitedRatePerDay}</h5>
                              <h5 className="discounted-price">${car.defaultUnlimitedRatePerDay}</h5>
                            </>
                          ) : (
                            <h5>${car.defaultUnlimitedRatePerDay || "0.00"}</h5>
                          )}

                          <span className="rate-label">{car.displayUnlimitedRateLabel || "7+ Days Rate"}</span>
                        </div>

                        <p>Unlimited KM</p>

                        <a
                          data-analytics-name={`Select Car - ${car.name} - Unlimited KM`}
                          onClick={() => {
                            if (!car.isUnavailable && !car.isSold) onSelectingCar(car, "unlimited");
                          }}
                          className={`BtnFill cursor-pointer ${
                            car.isSold || car.isUnavailable ? "disabled-link" : ""
                          }`}
                        >
                          {car.unavailableLabel || (car.isSold ? "Sold Out" : "Select")}
                        </a>

                        {car.isSold && !car.isUnavailable && (
                          <span
                            data-analytics-name={`View Availability - ${car.name} - Unlimited`}
                            className="view-availability-link"
                            onClick={() => handleViewAvailability(car)}
                          >
                            View Availability
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AvailabilityModal
        isOpen={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        car={selectedCarForAvailability}
      />

      <Footer />
    </>
  );
};

export default Cars;
